/**
 * MapBox Markup Style Factory.
 *
 * MapBox has their own style specification for styling layers on the map:
 * https://docs.mapbox.com/mapbox-gl-js/style-spec/.  This specification
 * outlines a domain-specific language (DSL) for styling.  That DSL has some
 * flexibility and rigidity in varying places.  So for some styles, we're able
 * to leverage their "data-driven" styling features where we can pull features
 * from the markup data itself, such as color, line width, etc and use that
 * along with some DSL features like switch statements, to readily display
 * styles. Some features don't yet support data-driven styling or any branching
 * logic, so we have to generate the styles for displaying things like dashed
 * lines.  To further complicate that, dashed lines are displayed in
 * "line-width" units, so they dashes are different lengths depending on the
 * line widths.  So we need to generate combinations of styles in order for the
 * dashed lines to display consistently across the varying line widths.
 *
 * FUTURE: When line-dasharray supports data-driven styling, we could simplify
 * this factory a bit, especially if they also introduce the ability to query
 * the line width and divide the dash array by the current line-width to get a
 * consistent pixel dash length.  Keep an eye on this Github Issue:
 * https://github.com/mapbox/mapbox-gl-js/issues/3045 for updates.
 */

import { arrayDivide } from '@/lib/math'
import { STROKE_STYLE_MAP, STROKE_WEIGHT_MAP } from '@/lib/markup-styles'
import { isMapPinsEnabled } from '@/lib/prototyping-flags'
import { WAYPOINT_CLUSTER_LAYER_ID } from '@/lib/waypoint-clustering'

const DASH_STYLE = STROKE_STYLE_MAP.get('dash').style
const DOT_STYLE = STROKE_STYLE_MAP.get('dot').style
const THIN_WIDTH = STROKE_WEIGHT_MAP.get('thin').weight
const NORMAL_WIDTH = STROKE_WEIGHT_MAP.get('normal').weight
const THICK_WIDTH = STROKE_WEIGHT_MAP.get('thick').weight

const THIN_HIGHLIGHT_WIDTH = THIN_WIDTH + 2
const NORMAL_HIGHLIGHT_WIDTH = NORMAL_WIDTH + 4
const THICK_HIGHLIGHT_WIDTH = THICK_WIDTH + 4
const HIGHLIGHT_COLOR = [255, 255, 255]

// Fallback fill opacity used only when a shape's `fill_color` array carries no
// alpha channel for some reason.
const DEFAULT_SHAPE_FILL_OPACITY = 0.1

/**
 * Creates area fill styles.
 * @param {Object} [opts] - Optional parameters to configure the style layer.
 * @param {boolean} [opts.alphaFromColor] - When true, the feature's `fill_color`
 *   is an `[r, g, b, a]` array whose alpha carries the intended fill opacity:
 *   render the fill from the RGB channels and drive `fill-opacity` from that
 *   array's alpha (falling back to `DEFAULT_SHAPE_FILL_OPACITY` when absent).
 *   This uses the stored alpha as the fill opacity instead of compounding it with
 *   a fixed `fill-opacity`, which would otherwise collapse shape fills to ~0.02
 *   (invisible). When false, `fill_color` is used as-is with a fixed opacity
 *   (e.g. land areas store an opaque rgba string and rely on the fixed value).
 * @return {Array<Object>} Mapbox style layers.
 */
export const createFillStyles = (opts) => {
  const source = opts?.source
  const sourcePrefix = source ? `${source}-` : ''
  const propertyPrefix = opts?.propertyPrefix ?? ''
  const formatProperty = (property) => `${propertyPrefix}${property}`

  const fillColorProp = formatProperty('fill_color')

  const fillColor = opts?.alphaFromColor
    ? [
        'rgb',
        ['at', 0, ['get', fillColorProp]],
        ['at', 1, ['get', fillColorProp]],
        ['at', 2, ['get', fillColorProp]],
      ]
    : ['get', fillColorProp]

  const fillOpacity = opts?.alphaFromColor
    ? [
        'case',
        ['>=', ['length', ['get', fillColorProp]], 4],
        ['at', 3, ['get', fillColorProp]],
        DEFAULT_SHAPE_FILL_OPACITY,
      ]
    : DEFAULT_SHAPE_FILL_OPACITY

  const layer = {
    id: `${sourcePrefix}fill`,
    type: 'fill',
    paint: {
      'fill-color': fillColor,
      'fill-opacity': fillOpacity,
    },
    filter: ['has', fillColorProp],
  }

  if (source) {
    layer.source = source
  }

  return [layer]
}

/**
 * Creates line styles.
 * @param {Object} [opts] - Optional parameters to configure the style layer.
 * @return {Array<Object>} Mapbox style layers.
 */
export const createLineStyles = (opts) => {
  const source = opts?.source
  const sourcePrefix = source ? `${source}-` : ''
  const propertyPrefix = opts?.propertyPrefix ?? ''
  const formatProperty = (property) => `${propertyPrefix}${property}`

  const styleProp = formatProperty('style')
  const weightProp = formatProperty('weight')
  const colorProp = formatProperty('color')
  const highlightedProp = formatProperty('highlighted')
  const opacityProp = formatProperty('opacity')

  const isDashLine = ['==', styleProp, 'dash']
  const isDotLine = ['==', styleProp, 'dot']
  const isThinLine = ['==', weightProp, 'thin']
  const isNormalLine = ['==', weightProp, 'normal']
  const isThickLine = ['==', weightProp, 'thick']

  const lineBase = {
    type: 'line',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['get', colorProp],
      'line-opacity': [
        'case',
        ['has', opacityProp],
        ['get', opacityProp],
        /* fallback */ 1,
      ],
    },
  }

  const layers = [
    {
      ...lineBase,
      id: `${sourcePrefix}highlight-line`,
      filter: ['all', ['has', highlightedProp], ['==', highlightedProp, true]],
      paint: {
        ...lineBase.paint,
        'line-color': ['rgb', ...HIGHLIGHT_COLOR],
        'line-width': [
          'case',
          ['==', ['get', weightProp], 'thin'],
          THIN_HIGHLIGHT_WIDTH,
          ['==', ['get', weightProp], 'normal'],
          NORMAL_HIGHLIGHT_WIDTH,
          ['==', ['get', weightProp], 'thick'],
          THICK_HIGHLIGHT_WIDTH,
          /* fallback */ NORMAL_HIGHLIGHT_WIDTH,
        ],
      },
    },

    {
      ...lineBase,
      id: `${sourcePrefix}solid-lines`,
      filter: ['all', ['has', styleProp], ['==', styleProp, 'solid']],
      paint: {
        ...lineBase.paint,
        'line-width': [
          'case',
          ['==', ['get', weightProp], 'thin'],
          THIN_WIDTH,
          ['==', ['get', weightProp], 'normal'],
          NORMAL_WIDTH,
          ['==', ['get', weightProp], 'thick'],
          THICK_WIDTH,
          /* fallback */ NORMAL_WIDTH,
        ],
      },
    },
    {
      ...lineBase,
      id: `${sourcePrefix}thin-dash-lines`,
      filter: [
        'all',
        ['has', weightProp],
        isThinLine,
        ['has', styleProp],
        isDashLine,
      ],
      paint: {
        ...lineBase.paint,
        'line-width': THIN_WIDTH,
        'line-dasharray': arrayDivide(DASH_STYLE, THIN_WIDTH),
      },
    },
    {
      ...lineBase,
      id: `${sourcePrefix}normal-dash-lines`,
      filter: [
        'all',
        ['has', weightProp],
        isNormalLine,
        ['has', styleProp],
        isDashLine,
      ],
      paint: {
        ...lineBase.paint,
        'line-width': NORMAL_WIDTH,
        'line-dasharray': arrayDivide(DASH_STYLE, NORMAL_WIDTH),
      },
    },
    {
      ...lineBase,
      id: `${sourcePrefix}thick-dash-lines`,
      filter: [
        'all',
        ['has', weightProp],
        isThickLine,
        ['has', styleProp],
        isDashLine,
      ],
      paint: {
        ...lineBase.paint,
        'line-width': THICK_WIDTH,
        'line-dasharray': arrayDivide(DASH_STYLE, THICK_WIDTH),
      },
    },
    {
      ...lineBase,
      id: `${sourcePrefix}thin-dot-lines`,
      filter: [
        'all',
        ['has', weightProp],
        isThinLine,
        ['has', styleProp],
        isDotLine,
      ],
      paint: {
        ...lineBase.paint,
        'line-width': THIN_WIDTH,
        'line-dasharray': arrayDivide(DOT_STYLE, THIN_WIDTH),
      },
    },
    {
      ...lineBase,
      id: `${sourcePrefix}normal-dot-lines`,
      filter: [
        'all',
        ['has', weightProp],
        isNormalLine,
        ['has', styleProp],
        isDotLine,
      ],
      paint: {
        ...lineBase.paint,
        'line-width': NORMAL_WIDTH,
        'line-dasharray': arrayDivide(DOT_STYLE, NORMAL_WIDTH),
      },
    },
    {
      ...lineBase,
      id: `${sourcePrefix}thick-dot-lines`,
      filter: [
        'all',
        ['has', weightProp],
        isThickLine,
        ['has', styleProp],
        isDotLine,
      ],
      paint: {
        ...lineBase.paint,
        'line-width': THICK_WIDTH,
        'line-dasharray': arrayDivide(DOT_STYLE, THICK_WIDTH),
      },
    },
  ]

  if (source) {
    layers.forEach((layer) => {
      layer.source = source
    })
  }

  return layers
}

/**
 * Creates icon image styles.
 * @param {Object} [opts] - Optional parameters to configure the style layer.
 * @return {Array<Object>} Mapbox style layers.
 */
export const createIconStyles = (opts) => {
  const source = opts?.source
  const sourcePrefix = source ? `${source}-` : ''
  const propertyPrefix = opts?.propertyPrefix ?? ''
  const formatProperty = (property) => `${propertyPrefix}${property}`

  const iconImageProp = formatProperty('icon_image')
  let iconImageZoomProp = formatProperty('icon_image_low_zoom')

  iconImageZoomProp = isMapPinsEnabled.value ? iconImageZoomProp : iconImageProp

  const symbolBase = {
    type: 'symbol',
    layout: {
      'icon-image': [
        'step',
        ['zoom'],
        ['get', iconImageZoomProp],
        14,
        ['get', iconImageProp],
      ],
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-size': 0.45,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-field': [
        'case',
        ['get', 'highlighted'],
        ['get', 'coordString'],
        '',
      ],
      'text-font': ['Open Sans Bold'],
      'text-padding': 0,
      'text-size': 14,
      'text-offset': [1.25, -0.6],
      'text-anchor': 'left',
      'text-justify': 'left',
    },
    paint: {
      'text-color': 'white',
      'text-halo-color': 'black',
      'text-halo-width': 2,
    },
  }

  const layer = {
    ...symbolBase,
    id: `${sourcePrefix}Icons`,
  }

  if (source) {
    layer.source = source
  }

  return [layer]
}

/**
 * Creates label styles.
 * @param {Object} [opts] - Optional parameters to configure the style layer.
 * @return {Array<Object>} Mapbox style layers.
 */
export const createLabelStyles = (opts) => {
  const source = opts?.source
  const sourcePrefix = source ? `${source}-` : ''

  const layer = {
    id: `${sourcePrefix}-label`,
    type: 'symbol',
    filter: ['all', ['has', 'meta'], ['==', 'meta', 'label']],
    layout: {
      'symbol-placement': 'point',
      'symbol-spacing': 5000,
      'symbol-avoid-edges': true,
      'text-anchor': 'center',
      'text-field': '{title}',
      'text-offset': ['get', 'text-offset'],
      'text-font': ['Open Sans Regular'],
      'text-padding': 0,
      'text-size': 16,
      // makes sure long labels are shown (value in ems)
      'text-max-width': 50,
    },
    paint: {
      'text-color': 'white',
      'text-halo-color': 'black',
      'text-halo-width': 2,
    },
  }

  const waypointLabel = {
    minzoom: 11,
    id: `${sourcePrefix}-waypoint-name-label`,
    type: 'symbol',
    filter: ['all', ['has', 'meta'], ['==', 'meta', 'waypoint-label']],
    layout: {
      'symbol-placement': 'point',
      'symbol-spacing': 5000,
      'symbol-avoid-edges': true,
      'text-anchor': 'center',
      'text-field': '{title}',
      'text-offset': [0, 1],
      'text-font': ['Open Sans Regular'],
      'text-padding': 0,
      'text-size': 16,
      // makes sure long labels are shown (value in ems)
      'text-max-width': 50,
    },
    paint: {
      'text-color': 'white',
      'text-halo-color': 'black',
      'text-halo-width': 2,
    },
  }

  if (source) {
    layer.source = source
    waypointLabel.source = source
  }

  return [layer, waypointLabel]
}

/**
 * Creates MapBox markup styles for various markup display combinations.
 * Layer-order matters.
 * @param {Object} [opts] - Optional parameters to configure the style layer.
 * @return {Array<Object>} Mapbox style layers
 */
export const createMarkupStyles = (opts) => [
  ...createLineStyles(opts),
  ...createLabelStyles(opts),
]

/**
 * Creates MapBox styles for waypoints
 * @param {Object} [opts] - Optional parameters to configure the style layer.
 * @return {Array<Object>} Mapbox style layers
 */
export const createWaypointStyles = (opts) => [
  ...createIconStyles(opts),
  ...createWaypointClusterLayer(opts),
]

/**
 * *(AI-generated)*
 * Creates MapBox label styles for waypoints.
 */
export const createWaypointLabelStyles = (opts) => [...createLabelStyles(opts)]

/**
 * Creates MapBox styles for highlighted waypoints
 * @param {Object} [opts] - Optional parameters to configure the style layer.
 * @return {Array<Object>} Mapbox style layers
 */
export const createHlWaypointStyles = (opts) => [
  ...createIconStyles(opts),
  ...createLabelStyles(opts),
]

/**
 * Creates MapBox styles for shapes
 * @param {Object} [opts] - Optional parameters to configure the style layer.
 * @return {Array<Object>} Mapbox style layers
 */
export const createShapeStyles = (opts) => [
  ...createFillStyles({ ...opts, alphaFromColor: true }),
  ...createLineStyles(opts),
]

/**
 * Creates MapBox styles for range radiuses
 * @param {Object} opts - optional parameters to configure the style layer
 * @returns {Array<Object>} Mapbox style layers
 */
export const createRangeRadiusStyle = (opts) => [
  {
    id: 'radius',
    type: 'fill',
    source: opts.source,
    paint: {
      'fill-color': ['get', 'radius_color'],
      'fill-opacity': 0.3,
    },
    filter: ['all', ['has', 'class'], ['==', 'class', 'full-radius']],
    layout: {
      visibility: 'visible',
    },
  },
  {
    id: 'half-radius',
    type: 'fill',
    source: opts.source,
    paint: {
      'fill-color': '#ffffff',
      'fill-opacity': 0.2,
    },
    filter: ['all', ['has', 'class'], ['==', 'class', 'half-radius']],
    layout: {
      visibility: 'visible',
    },
  },
  {
    id: 'outline',
    type: 'line',
    source: opts.source,
    paint: {
      'line-color': ['get', 'radius_color'],
      'line-width': 1.5,
    },
    filter: ['has', 'radius_color'],
    layout: {
      visibility: 'visible',
    },
  },
  {
    id: 'crosshair',
    type: 'line',
    source: opts.source,
    paint: {
      'line-color': '#ffffff',
      'line-width': 0.5,
    },
    filter: ['all', ['has', 'class'], ['==', 'class', 'crosshair']],
    layout: {
      visibility: 'visible',
    },
  },
  {
    id: 'full-label',
    type: 'symbol',
    source: opts.source,
    paint: {
      'text-color': '#ffffff',
    },
    filter: ['all', ['has', 'id'], ['==', 'id', 'full-label']],
    layout: {
      visibility: 'visible',
      'symbol-placement': 'point',
      'symbol-spacing': 1000000,
      'text-font': ['Open Sans Bold'],
      'text-field': ['get', 'full_label'],
      'text-offset': [-0.6, -0.6],
      'text-anchor': 'right',
      'text-justify': 'left',
      'text-size': 14,
    },
  },
  {
    id: 'half-label',
    type: 'symbol',
    source: opts.source,
    paint: {
      'text-color': '#ffffff',
    },
    filter: ['all', ['has', 'id'], ['==', 'id', 'half-label']],
    layout: {
      visibility: 'visible',
      'symbol-placement': 'point',
      'symbol-spacing': 1000000,
      'text-font': ['Open Sans Bold'],
      'text-field': ['get', 'half_label'],
      'text-offset': [-0.6, -0.6],
      'text-anchor': 'right',
      'text-justify': 'left',
      'text-size': 14,
    },
  },
]

/**
 * Creates MapBox styles for wind markers
 * @param {Object} opts - optional parameters to configure the style layer
 * @returns {Array<Object>} Mapbox style layers
 */
export const createWindMarkerStyle = (opts) => {
  const filter = ['all', ['has', 'wind_icon'], ['has', 'wind_text']]
  if (isMapPinsEnabled.value) {
    filter.push(['>=', ['zoom'], 14])
  }
  const id = `${opts.source}-wind`

  return [
    {
      id,
      type: 'symbol',
      source: opts.source,
      filter: filter,
      layout: {
        'symbol-placement': 'point',
        'text-size': 12.5,
        'text-field': [
          'format',
          ['get', 'wind_text'],
          {
            'text-font': ['literal', ['Roboto Bold']],
            'font-scale': 1,
            'text-color': [
              'case',
              ['>', ['length', ['get', 'wind_additional_text']], 0],
              '#FFFFFF',
              ['==', ['get', 'wind_status'], 'Good'],
              '#65B604',
              ['==', ['get', 'wind_status'], 'OK'],
              '#F5A623',
              ['==', ['get', 'wind_status'], 'Bad'],
              '#FF3300',
              '#FFFFFF',
            ],
          },
          '  ',
          {
            'text-font': ['literal', ['Roboto Bold']],
            'font-scale': 1,
          },
          ['get', 'wind_additional_text'],
          {
            'text-font': ['literal', ['Roboto Bold']],
            'font-scale': 1,
            'text-color': [
              'match',
              ['get', 'wind_additional_text'],
              'Good Wind',
              '#65B604',
              'OK Wind',
              '#F5A623',
              'Bad Wind',
              '#FF3300',
              '#FFFFFF',
            ],
          },
        ],
        'text-offset': [
          'case',
          ['>', ['length', ['get', 'wind_additional_text']], 0],
          ['literal', [0.75, -10.25]],
          ['literal', [0.75, -5.45]],
        ],
        'icon-offset': [
          'case',
          ['>', ['length', ['get', 'wind_additional_text']], 0],
          ['literal', [0, -130]],
          ['literal', [0, -70]],
        ],
        'text-anchor': 'center',
        'text-justify': 'left',
        'icon-image': ['get', 'wind_icon'],
        'icon-allow-overlap': true,
        'text-allow-overlap': false,
      },
      paint: {
        'text-translate-anchor': 'viewport',
        'icon-translate-anchor': 'viewport',
      },
    },
  ]
}

/**
 * Creates MapBox styles for wind markers on highlighted waypoints
 * @param {Object} opts - optional parameters to configure the style layer
 * @returns {Array<Object>} Mapbox style layers
 */
export const createHighlightedWindMarkerStyle = (opts) => [
  ...createWindMarkerStyle(opts),
]

/**
 * Creates MapBox styles for Elevation Profile scrubbing point
 * @param {Object} opts - optional parameters to configure the style layer
 * @returns {Array<Object>} Mapbox style layers
 */
export const createElevationPointStyle = (opts) => [
  {
    id: 'elevation-point',
    type: 'circle',
    source: opts.source,
    paint: {
      'circle-color': '#ffffff',
      'circle-radius': 7,
      'circle-stroke-color': '#000000',
      'circle-stroke-width': 2,
    },
  },
  {
    id: 'elevation-point-label',
    type: 'symbol',
    source: opts.source,
    filter: [
      'all',
      ['has', 'distance_point_number'],
      ['has', 'distance_point_unit'],
      ['has', 'elevation_point_number'],
      ['has', 'elevation_point_unit'],
    ],
    layout: {
      'symbol-placement': 'point',
      'symbol-spacing': 1000000,
      'text-size': 11,
      'text-line-height': 1.3,
      'text-font': ['literal', ['Roboto Regular']],
      'text-field': [
        'format',
        'Dist: ',
        ['get', 'distance_point_number'],
        ['get', 'distance_point_unit'],
        ' | ',
        {
          'text-color': '#464646',
          'font-scale': 1.5,
        },
        ' Elev: ',
        ['get', 'elevation_point_number'],
        ['get', 'elevation_point_unit'],
      ],
      'text-offset': [2, 0],
      'text-anchor': 'left',
      'text-justify': 'left',
      'text-max-width': 32,
      'icon-text-fit': 'both',
      'icon-image': ['get', 'tooltip_pill'],
      'icon-allow-overlap': true,
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': '#ffffff',
    },
  },
]

/**
 * Creates styles for edit features.
 * @return {Array<Object>} Mapbox style layers.
 */
const createLineEditStyles = (opt) => [
  {
    id: 'line-vertex',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'vertex'],
    ],
    paint: {
      'circle-radius': [
        'case',
        ['has', 'circleRadius'],
        ['get', 'circleRadius'],
        5,
      ],
      'circle-color': [
        'case',
        ['has', 'colorInner'],
        ['get', 'colorInner'],
        '#FFFFFF',
      ],
      'circle-stroke-color': ['get', 'color'],
      'circle-stroke-width': 2,
    },
  },
  {
    id: 'line-hover',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'hover'],
    ],
    paint: {
      'circle-radius': 7,
      'circle-color': '#1e90ff',
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2,
    },
  },
  {
    id: 'trim-start',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'trim-start'],
    ],
    paint: {
      'circle-radius': 9,
      'circle-color': '#65b604',
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2,
    },
  },
  {
    id: 'trim-end',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'trim-end'],
    ],
    paint: {
      'circle-radius': 9,
      'circle-color': '#c92800',
    },
  },
  {
    id: 'connect-point',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'connect-point'],
    ],
    paint: {
      'circle-radius': 10,
      'circle-color': '#FFFFFF',
      'circle-stroke-color': '#222222',
      'circle-stroke-width': 1,
    },
  },
  {
    id: 'point-label',
    type: 'symbol',
    source: opt.source,
    filter: ['all', ['has', 'meta'], ['==', 'meta', 'point-label']],
    layout: {
      'text-ignore-placement': true,
      'symbol-placement': 'point',
      'text-field': '{title}',
      'text-font': ['Open Sans Bold'],
      'text-size': 16,
    },
    paint: {
      'text-color': 'black',
    },
  },
]

/**
 * Styles for route features.
 * @return {Array<Object>} Mapbox style layers.
 */
const createRouteStyles = (opt) => [
  {
    id: 'route-preview&highlight',
    type: 'line',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'LineString'],
      ['has', 'meta'],
      ['==', 'meta', 'route-preview'],
    ],
    paint: {
      'line-color': ['rgb', ...HIGHLIGHT_COLOR],
      'line-width': [
        'case',
        ['==', ['get', 'route_weight'], THIN_WIDTH],
        THIN_HIGHLIGHT_WIDTH,
        ['==', ['get', 'route_weight'], NORMAL_WIDTH],
        NORMAL_HIGHLIGHT_WIDTH,
        ['==', ['get', 'route_weight'], THICK_WIDTH],
        THICK_HIGHLIGHT_WIDTH,
        /* fallback */ NORMAL_HIGHLIGHT_WIDTH,
      ],
      'line-opacity': ['get', 'highlight_opacity'],
    },
  },
  {
    id: 'route-preview',
    type: 'line',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'LineString'],
      ['has', 'meta'],
      ['==', 'meta', 'route-preview'],
    ],
    paint: {
      'line-color': ['get', 'route_color'],
      'line-dasharray': [
        'case',
        ['==', ['get', 'route_style'], 'dash'],
        ['literal', arrayDivide(DASH_STYLE, NORMAL_WIDTH)],
        ['==', ['get', 'route_style'], 'dot'],
        ['literal', arrayDivide(DOT_STYLE, NORMAL_WIDTH)],
        /* fallback */ ['literal', []],
      ],
      'line-width': [
        'case',
        ['==', ['get', 'route_weight'], THIN_WIDTH],
        THIN_WIDTH,
        ['==', ['get', 'route_weight'], NORMAL_WIDTH],
        NORMAL_WIDTH,
        ['==', ['get', 'route_weight'], THICK_WIDTH],
        THICK_WIDTH,
        /* fallback */ NORMAL_WIDTH,
      ],
    },
  },
  {
    id: 'route-waypoint-selected',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'route-waypoint-selected'],
    ],
    paint: {
      'circle-radius': 16,
      'circle-color': ['get', 'circle_color'],
      'circle-opacity': 0.5,
    },
  },
  {
    id: 'route-hover-point',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'route-hover-point'],
    ],
    paint: {
      'circle-radius': 7,
      'circle-color': '#ffffff',
      'circle-stroke-color': '#000000',
      'circle-stroke-width': 2,
    },
  },
  {
    id: 'route-waypoint',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'route-waypoint'],
    ],
    paint: {
      'circle-radius': 7,
      'circle-color': [
        'case',
        ['has', 'colorInner'],
        ['get', 'colorInner'],
        '#000000',
      ],
      'circle-stroke-color': [
        'case',
        ['has', 'color'],
        ['get', 'color'],
        '#FFFFFF',
      ],
      'circle-stroke-width': 2,
      'circle-opacity': ['get', 'opacity'],
    },
  },
  {
    id: 'route-hover-point-label',
    type: 'symbol',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'route-hover-point-label'],
    ],
    layout: {
      'symbol-placement': 'point',
      'symbol-spacing': 1000000,
      'text-size': 11,
      'text-line-height': 1.45,
      'text-font': ['literal', ['Roboto Regular']],
      'text-field': [
        'format',
        ['get', 'label_text'],
        {
          'text-font': ['literal', ['Roboto Bold']],
        },
        'Dist: ',
        ['get', 'distance_point_number'],
        ['get', 'distance_point_unit'],
        ' | ',
        {
          'text-color': '#464646',
          'font-scale': 1.5,
        },
        ' Elev: ',
        ['get', 'elevation_point_number'],
        ['get', 'elevation_point_unit'],
      ],

      'text-offset': [
        'case',
        ['==', ['get', 'is_marker_label'], true],
        ['literal', [2.6, 0]],
        ['literal', [2, 0]],
      ],
      'text-anchor': 'left',
      'text-justify': 'left',
      'icon-text-fit': 'both',
      'icon-image': ['get', 'tooltip_image'],
      'icon-allow-overlap': true,
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': '#ffffff',
    },
  },
  {
    id: 'route-drag-label',
    type: 'symbol',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'route-drag-label'],
    ],
    layout: {
      'symbol-placement': 'point',
      'symbol-spacing': 1000000,
      'text-size': 11,
      'text-line-height': 1.45,
      'text-font': ['literal', ['Roboto Regular']],
      'text-field': [
        'format',
        'New Route\n',
        {
          'text-font': ['literal', ['Roboto Bold']],
        },
        '. \n',
        {
          'text-color': '#222222',
          'font-scale': 0.3,
        },
        'Total Dist: ',
        ['get', 'distance_point_number'],
        ['get', 'distance_point_unit'],
        '\n',
        ['get', 'distance_difference'],
        {
          'text-font': ['literal', ['Roboto Italic']],
        },
      ],
      'text-offset': [2.6, 0],
      'text-anchor': 'left',
      'text-justify': 'left',
      'icon-text-fit': 'both',
      'icon-image': ['get', 'tooltip_image'],
      'icon-allow-overlap': true,
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': '#ffffff',
    },
  },
]

/**
 * Creates the full set of Mapbox GL Draw styles for displaying markups in edit and view modes.
 * @return {Array<Object>} Mapbox style layer objects.
 */
export const createEditMarkupStyles = (opt) => {
  return [
    ...createFillStyles({ ...opt, alphaFromColor: true }),
    ...createLineStyles(opt),
    ...createIconStyles(opt),
    ...createLineEditStyles(opt),
    ...createRouteStyles(opt),
    ...createLabelStyles(opt),
  ]
}

/**
 * *(AI-generated)*
 * Creates MapBox styles for offline map area boundaries.
 */
export const createOfflineStyle = (opt) => [
  {
    id: 'offline-maps$highlight',
    type: 'line',
    source: opt.source,
    paint: {
      'line-color': '#FFFFFF',
      'line-width': 6,
      'line-opacity': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        1,
        0,
      ],
    },
  },
  {
    id: 'offline-maps',
    type: 'line',
    source: opt.source,
    paint: {
      'line-color': '#11FF00',
      'line-width': 2,
    },
  },
]

/**
 * *(AI-generated)*
 * Creates MapBox styles for the offline map overlay with dimension labels.
 */
export const createOfflineOverlayStyle = (opt) => [
  {
    id: 'offline-overlay-fill',
    type: 'fill',
    source: opt.source,
    paint: {
      'fill-color': '#222222',
      'fill-opacity': 0.7,
    },
  },
  {
    id: 'offline-overlay-line',
    type: 'line',
    source: opt.source,
    paint: {
      'line-color': ['get', 'color'],
      'line-width': 2,
    },
  },
  {
    id: `map-width-label`,
    type: 'symbol',
    filter: ['all', ['has', 'meta'], ['==', 'meta', 'widthLabel']],
    layout: {
      'symbol-placement': 'point',
      'text-anchor': 'center',
      'text-field': '{title}',
      'text-font': ['Atlas Grotesk Black'],
    },
    source: opt.source,
    paint: {
      'text-color': 'white',
      'text-translate': [0, -14],
    },
  },
  {
    id: `map-height-label`,
    type: 'symbol',
    filter: ['all', ['has', 'meta'], ['==', 'meta', 'heightLabel']],
    layout: {
      'symbol-placement': 'point',
      'text-anchor': 'center',
      'text-field': '{title}',
      'text-font': ['Atlas Grotesk Black'],
      'text-rotate': -90,
    },
    source: opt.source,
    paint: {
      'text-color': 'white',
      'text-translate': [-14, 0],
    },
  },
]

/**
 * *(AI-generated)*
 * Creates MapBox styles for highlighted geometry features.
 */
export const createHighlightStyles = (opt) => [
  {
    id: 'highlight-halo',
    type: 'line',
    source: opt.source,
    paint: {
      'line-color': 'hsl(0, 0%, 100%)',
      'line-width': 4.5,
    },
  },
  {
    id: 'highlight',
    type: 'line',
    source: opt.source,
    paint: {
      'line-color': 'hsl(83, 100%, 42%)',
      'line-width': 2.5,
    },
  },
  {
    id: 'highlight-line-vertex',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'vertex'],
    ],
    paint: {
      'circle-radius': 10,
      'circle-color': 'white',
    },
  },
  {
    id: 'highlight-route-waypoint',
    type: 'circle',
    source: opt.source,
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['has', 'meta'],
      ['==', 'meta', 'route-waypoint'],
    ],
    paint: {
      'circle-radius': 16,
      'circle-color': 'white',
      'circle-opacity': 0.3,
    },
  },
]

/**
 * *(AI-generated)*
 * Creates the MapBox sky gradient style layer for 3D map views.
 */
export const createSkyStyleLayer = () => ({
  id: 'sky',
  type: 'sky',
  paint: {
    'sky-type': 'gradient',
    'sky-gradient': [
      'interpolate',
      ['linear'],
      ['sky-radial-progress'],
      0.8,
      'rgba(135, 206, 235, 1.0)',
      1,
      'rgba(0,0,0,0.1)',
    ],
    'sky-gradient-center': [0, 0],
    'sky-gradient-radius': 90,
    'sky-opacity': ['interpolate', ['exponential', 0.1], ['zoom'], 5, 0, 22, 1],
  },
})

/**
 * *(AI-generated)*
 * Creates the MapBox symbol layer for displaying clustered waypoint counts.
 */
export const createWaypointClusterLayer = (opts) => [
  {
    id: WAYPOINT_CLUSTER_LAYER_ID,
    type: 'symbol',
    source: 'waypoints',
    filter: ['has', 'point_count'],

    layout: {
      'icon-image': 'cluster_icon',
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-size': 1,
      'icon-offset': [0, 34],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-field': [
        'case',
        ['<=', ['get', 'point_count'], 99],
        ['get', 'point_count'],
        ['>', ['get', 'point_count'], 99],
        '99+',
        ['get', 'point_count'],
      ],
      'text-font': ['Open Sans Bold'],
      'text-size': 14,
    },
    paint: {
      'text-color': opts.clusterIconTextColor,
    },
  },
]
